import React, { useState, useEffect } from 'react';
import './PaginationDropdown.scss';

interface PaginationDropdownProps {
  /** Current items per page value */
  itemsPerPage: number;
  /** Function to handle items per page change */
  onItemsPerPageChange: (itemsPerPage: number) => void;
  /** Available options */
  options?: number[];
  /** Custom class name */
  className?: string;
  /** Custom label */
  label?: string;
}

/**
 * Generic Pagination dropdown component
 * Allows users to select how many items to display per page
 */
const PaginationDropdown: React.FC<PaginationDropdownProps> = ({
  itemsPerPage,
  onItemsPerPageChange,
  options = [10, 25, 50, 100],
  className = '',
  label = 'Show'
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.pagination-dropdown-container')) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  return (
    <div className={`pagination-dropdown-container ${className}`}>
      <button 
        className="pagination-dropdown-button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowDropdown(!showDropdown);
        }}
        type="button"
      >
        {label} {itemsPerPage}
        <span className="dropdown-arrow">▼</span>
      </button>
      
      {showDropdown && (
        <div 
          className="pagination-dropdown-menu"
          onClick={(e) => e.stopPropagation()}
        >
          {options.map(option => (
            <button
              key={option}
              className={`dropdown-option ${itemsPerPage === option ? 'selected' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔍 DEBUG: Pagination dropdown clicked, changing to:', option);
                onItemsPerPageChange(option);
                setShowDropdown(false);
              }}
              type="button"
            >
              {label} {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaginationDropdown;
